import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { SquaresFour, FolderSimple, Globe, ChartLineUp, CreditCard, UserCircle, SignOut, Cube, ShieldStar, HardDrives } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/dashboard", label: "Dashboard", Icon: SquaresFour },
  { to: "/projects", label: "Projects", Icon: FolderSimple },
  { to: "/domains", label: "Domains", Icon: Globe },
  { to: "/usage", label: "Usage", Icon: ChartLineUp },
  { to: "/billing", label: "Billing", Icon: CreditCard },
  { to: "/account", label: "Account", Icon: UserCircle },
];

export function Logo({ size = 20 }) {
  return (
    <div className="flex items-center gap-2">
      <div className="grid h-8 w-8 place-items-center rounded-xl bg-primary text-primary-foreground">
        <Cube size={size} weight="fill" />
      </div>
      <span className="font-display text-lg font-bold tracking-tight">Hostless</span>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="hidden w-64 shrink-0 flex-col border-r border-border bg-card px-4 py-6 md:flex">
        <div className="px-2"><Logo /></div>
        <nav className="mt-10 flex flex-1 flex-col gap-1">
          {NAV.map(({ to, label, Icon }) => (
            <NavLink key={to} to={to} data-testid={`nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
                  isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}>
              <Icon size={20} weight="duotone" />
              {label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink to="/admin" data-testid="nav-admin"
              className={({ isActive }) =>
                `mt-2 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
                  isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}>
              <ShieldStar size={20} weight="duotone" /> Admin
            </NavLink>
          )}
          {user?.is_owner && (
            <NavLink to="/owner/infrastructure" data-testid="nav-infrastructure"
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
                  isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}>
              <HardDrives size={20} weight="duotone" /> Infrastructure
            </NavLink>
          )}
        </nav>
        <div className="mt-auto border-t border-border pt-4">
          <div className="mb-3 px-3">
            <p className="truncate text-sm font-semibold">{user?.name}</p>
            <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
          </div>
          <Button variant="ghost" onClick={handleLogout} data-testid="logout-button"
            className="w-full justify-start gap-3 rounded-xl text-muted-foreground hover:text-foreground">
            <SignOut size={20} weight="duotone" /> Sign out
          </Button>
        </div>
      </aside>
      <main className="flex-1 overflow-x-hidden">{children}</main>
    </div>
  );
}
